/* Cliente de persistencia AI para admin_v2.
   Mantiene los nombres publicos usados por las vistas, pero la comunicacion
   ahora pasa por public-api-boston /admin/ai usando la capa de servicios local. */
import { request } from '../../../services'
import { readSession } from '../../../services/session'

const LS_KEY = 'boston_dev_queue_v1'
const API_KEY_SETTING = 'anthropic_api_key'
let pendingDevQueueSummary = null
let cachedDevQueueSummary = { count: 0, ids: [], latestTs: '', via: 'cache', warn: '' }
let lastDevQueueSummaryAt = 0

function notifyDevQueueChanged() {
  try {
    window.dispatchEvent(new CustomEvent('boston-dev-review-queue-changed'))
  } catch (_) {}
}

function cacheDevQueueItem(item, syncState = 'local') {
  if (!item?.id) return
  try {
    const queue = JSON.parse(localStorage.getItem(LS_KEY) || '[]')
    const nextItem = { ...item, _syncState: syncState }
    const index = queue.findIndex((entry) => String(entry?.id || '') === String(item.id))
    if (index >= 0) queue[index] = { ...queue[index], ...nextItem }
    else queue.push(nextItem)
    localStorage.setItem(LS_KEY, JSON.stringify(queue.slice(-100)))
    cachedDevQueueSummary = { count: 0, ids: [], latestTs: '', via: 'cache', warn: '' }
    lastDevQueueSummaryAt = 0
    notifyDevQueueChanged()
  } catch (_) {}
}

function mergeDevQueueItems(dbItems, localItems) {
  const merged = new Map()
  ;(localItems || []).forEach((item) => {
    if (item?.id) merged.set(String(item.id), item)
  })
  ;(dbItems || []).forEach((item) => {
    if (item?.id) merged.set(String(item.id), { ...item, _syncState: 'db' })
  })
  return Array.from(merged.values()).sort((left, right) => {
    const leftDate = String(left?.updatedDate || left?.updated_date || left?.ts || left?.createdDate || left?.created_date || '')
    const rightDate = String(right?.updatedDate || right?.updated_date || right?.ts || right?.createdDate || right?.created_date || '')
    return leftDate.localeCompare(rightDate)
  })
}

function currentUser() {
  return readSession().email || 'front'
}

function controlledUnsupported(message) {
  const error = new Error(message)
  error.controlled = true
  return error
}

async function callAI(action, { params = {}, body = null } = {}) {
  const res = await request(action, body ? { method: 'POST', body } : { params })
  if (!res.ok) {
    const detail = res.data?.error || res.data?.message || 'No se pudo consumir /admin/ai'
    throw new Error(detail)
  }
  return res.data
}

function stringifyJSON(value, fallback = '') {
  if (value === undefined || value === null || value === '') return fallback
  return typeof value === 'string' ? value : JSON.stringify(value)
}

function safeParse(value) {
  if (!value || typeof value !== 'string') return value || null
  try { return JSON.parse(value) } catch (_) { return null }
}

function historyArray(value) {
  const history = safeParse(value)
  if (!history) return []
  if (Array.isArray(history)) return history
  if (Array.isArray(history.events)) return history.events
  if (history.source || history.target || history.action) return [history]
  return []
}

function historyTarget(value) {
  const direct = safeParse(value)
  if (direct && !Array.isArray(direct) && direct.target) return direct.target
  return historyArray(value).find((entry) => entry && entry.target)?.target || {}
}

function historySource(value) {
  const direct = safeParse(value)
  if (direct && !Array.isArray(direct) && direct.source) return direct.source
  const entry = historyArray(value).find((item) => item && item.source)
  return entry?.source || ''
}

function historyArtifactJson(value, names = []) {
  const pick = (item) => {
    if (!item || typeof item !== 'object') return ''
    for (const name of names) {
      if (item[name] !== undefined && item[name] !== null && String(item[name]).trim() !== '') return item[name]
    }
    return ''
  }
  const direct = safeParse(value)
  if (direct && !Array.isArray(direct)) {
    return pick(direct)
  }
  return pick(historyArray(value).find((item) => pick(item)))
}

function historyCatalogsJson(value) {
  return historyArtifactJson(value, ['catalogsJson', 'catalogs_json', 'catalogJson', 'catalog_json'])
}

function historyLogicJson(value) {
  return historyArtifactJson(value, ['logicJson', 'logic_json'])
}

function historyWithReviewArtifacts(history, files = {}) {
  const parsed = safeParse(history)
  const envelope = parsed && !Array.isArray(parsed) && typeof parsed === 'object'
    ? { ...parsed }
    : { events: Array.isArray(parsed) ? parsed : [] }
  if (files['catalogs.json'] !== undefined && String(files['catalogs.json'] || '').trim()) {
    envelope.catalogsJson = safeParse(files['catalogs.json']) || String(files['catalogs.json'])
  }
  if (files['logic.json'] !== undefined && String(files['logic.json'] || '').trim()) {
    envelope.logicJson = safeParse(files['logic.json']) || String(files['logic.json'])
  }
  return envelope
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value === undefined || value === null ? '' : value).trim()
    if (text) return text
  }
  return ''
}

function normalizeSettings(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.settings)) return data.settings
  return []
}

function normalizePromptContent(data) {
  if (!data) return ''
  return data.content || data.prompt || data.data?.content || ''
}

function normalizePromptRows(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.prompts)) return data.prompts
  return []
}

function normalizeQueueItem(row) {
  const dataJson = row.dataJson ?? row.data_json ?? ''
  const parsedData = safeParse(dataJson)
  const irJson = row.irJson ?? row.ir_json ?? ''
  const fichaMd = row.fichaMd ?? row.ficha_md ?? ''
  const appJs = row.appJs ?? row.app_js ?? ''
  const feedback = row.feedback || ''
  const messages = Array.isArray(row.messages)
    ? row.messages.map((message) => ({
      id: message.id || '',
      reviewId: message.reviewId || message.review_id || row.id || '',
      senderType: message.senderType || message.sender_type || '',
      senderUsername: message.senderUsername || message.sender_username || '',
      message: message.message || '',
      readByClient: message.readByClient === true || message.read_by_client === true || message.readByClient === 1 || message.read_by_client === 1,
      readByDev: message.readByDev === true || message.read_by_dev === true || message.readByDev === 1 || message.read_by_dev === 1,
      createdDate: message.createdDate || message.created_date || null,
    }))
    : []
  const sourceFiles = Array.isArray(row.sourceFiles || row.source_files) ? (row.sourceFiles || row.source_files) : []
  const parsedHistory = safeParse(row.history) || []
  const catalogsJson = row.catalogsJson ?? row.catalogs_json ?? row.catalogJson ?? row.catalog_json ?? historyCatalogsJson(row.history) ?? ''
  const logicJson = row.logicJson ?? row.logic_json ?? historyLogicJson(row.history) ?? ''
  const target = historyTarget(parsedHistory)
  const origin = row.origin || row.source || historySource(parsedHistory) || 'admin_v2'
  const createdUser = row.createdUser || row.created_user || null
  const dashboardCreatedUser = /dashboard/i.test(String(origin || '')) ? createdUser : null
  const session = parsedData?._session || {}
  let cachedFiles = {}
  try {
    const cached = JSON.parse(localStorage.getItem(LS_KEY) || '[]')
    const cachedItem = Array.isArray(cached) ? cached.find((item) => item && item.id === row.id) : null
    cachedFiles = cachedItem && cachedItem.files && typeof cachedItem.files === 'object' ? cachedItem.files : {}
  } catch (_) {
    cachedFiles = {}
  }
  const dbFiles = Object.assign(
    { 'data.json': dataJson || '', 'feedback.txt': feedback },
    irJson ? { 'ir.json': irJson } : {},
    catalogsJson ? { 'catalogs.json': typeof catalogsJson === 'string' ? catalogsJson : stringifyJSON(catalogsJson, '') } : {},
    logicJson ? { 'logic.json': typeof logicJson === 'string' ? logicJson : stringifyJSON(logicJson, '') } : {},
    fichaMd ? { 'FICHA.md': fichaMd } : {},
    appJs ? { 'app.js': appJs } : {},
  )

  return {
    id: row.id,
    ts: row.createdDate || row.created_date || row.ts || null,
    title: row.title || 'Revision sin titulo',
    feedback,
    messages,
    sourceFiles,
    sourceFileIds: sourceFiles.map((file) => file?.id).filter(Boolean),
    estado: row.estado || 'en_revision',
    data: parsedData,
    ir: safeParse(irJson),
    published: row.scriptUuid || row.script_uuid ? { uuid: row.scriptUuid || row.script_uuid } : null,
    files: { ...cachedFiles, ...dbFiles },
    original: dataJson || '',
    history: parsedHistory,
    scriptUuid: row.scriptUuid || row.script_uuid || null,
    username: row.username || row.user || session.username || target.username || target.user || dashboardCreatedUser || null,
    parentPcc: row.parentPcc || row.parent_pcc || session.parentPcc || session.parent_pcc || target.parentPcc || target.parent_pcc || null,
    pcc: row.pcc || session.pcc || target.pcc || null,
    agency: firstText(
      row.agency,
      row.agencyName,
      row.nameAgency,
      row.name_agency,
      parsedData?.agency,
      parsedData?.agencyName,
      session.agency,
      session.agencyName,
      session.nameAgency,
      session.name_agency,
      session.name,
      target.agency,
      target.agencyName,
      target.nameAgency,
      target.name_agency,
    ) || null,
    assignmentType: row.assignmentType || row.assignment_type || target.assignmentType || target.assignment_type || null,
    preferredLanguage: row.preferredLanguage || row.preferred_language || target.preferredLanguage || target.preferred_language || null,
    createdUser,
    origin,
    source: origin,
    _via: 'db',
  }
}

function normalizeKnowledgeRows(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.knowledge)) return data.knowledge
  return []
}

function normalizeCostRows(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.costs)) return data.costs
  if (Array.isArray(data?.rows)) return data.rows
  return []
}

function queuePayload(item) {
  const files = item.files || {}
  return {
    id: item.id,
    title: item.title || 'Revision sin titulo',
    estado: item.estado || 'en_revision',
    feedback: item.feedback || null,
    dataJson: files['data.json'] !== undefined ? String(files['data.json'] || '') : stringifyJSON(item.data, '{}'),
    irJson: files['ir.json'] !== undefined ? String(files['ir.json'] || '') : stringifyJSON(item.ir, ''),
    catalogsJson: files['catalogs.json'] || null,
    fichaMd: files['FICHA.md'] || null,
    appJs: files['app.js'] || null,
    origin: item.origin || item.source || historySource(item.history) || 'admin_v2',
    history: historyWithReviewArtifacts(item.history || [], files),
    scriptUuid: item.scriptUuid || item.script_uuid || null,
    username: item.username || (/dashboard/i.test(String(item.origin || item.source || historySource(item.history) || '')) ? item.createdUser : null) || null,
    parentPcc: item.parentPcc || item.parent_pcc || null,
    parent_pcc: item.parentPcc || item.parent_pcc || null,
    pcc: item.pcc || null,
    agency: item.agency || item.agencyName || item.nameAgency || item.name_agency || null,
    assignmentType: item.assignmentType || item.assignment_type || null,
    preferredLanguage: item.preferredLanguage || item.preferred_language || 'es',
    createdUser: item.createdUser || item.username || currentUser(),
    sourceFileIds: (item.sourceFileIds || item.sourceFiles || []).map((file) => typeof file === 'string' ? file : file?.id).filter(Boolean),
  }
}

function fileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo original.'))
    reader.onload = () => {
      const value = String(reader.result || '')
      resolve(value.includes(',') ? value.slice(value.indexOf(',') + 1) : value)
    }
    reader.readAsDataURL(file)
  })
}

export async function biaUploadSourceFile(file, options = {}) {
  if (!file) throw new Error('Archivo fuente requerido')
  const session = readSession()
  return callAI('ai_admin_create', {
    body: {
      resource: 'source-files',
      action: 'upload',
      data: {
        uploadId: options.uploadId || '',
        importId: options.importId || '',
        originalFilename: file.name || 'script.sst',
        fileRole: options.fileRole || '',
        contentType: file.type || 'application/octet-stream',
        contentBase64: await fileAsBase64(file),
        source: options.source || 'admin_v2_file_import',
        targetEnv: options.targetEnv || 'stg',
        createdUser: options.createdUser || currentUser(),
        username: options.username || session.username || session.email || '',
        parentPcc: options.parentPcc || session.parentPcc || session.parent_pcc || session.parentPCC || '',
        pcc: options.pcc || session.pcc || '',
      },
    },
  })
}

export async function biaDownloadSourceFile(id, reviewId = '') {
  if (!id) throw new Error('id de archivo fuente requerido')
  return callAI('ai_admin_create', {
    body: { resource: 'source-files', action: 'download', data: { id, reviewId } },
  })
}

export async function biaListSourceFiles(filter = {}) {
  const data = await callAI('ai_admin_create', {
    body: { resource: 'source-files', action: 'list', data: filter },
  })
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    page: Number(data?.page || 1),
    pageSize: Number(data?.pageSize || filter.pageSize || 25),
    total: Number(data?.total || 0),
  }
}

export async function biaDeleteSourceFile(id) {
  if (!id) throw new Error('id de archivo fuente requerido')
  return callAI('ai_admin_create', {
    body: { resource: 'source-files', action: 'delete', data: { id } },
  })
}

export async function biaFetch(resource, { method = 'GET', body = null, params = {} } = {}) {
  if (params.seed) {
    throw controlledUnsupported(`Seed de ${resource} pendiente: no existe endpoint /admin/ai permitido para seed en esta fase`)
  }

  if (resource === 'health') return callAI('ai_admin_get', { params: { resource: 'health', action: 'ping' } })

  if (resource === 'prompts') {
    if (method === 'GET') {
      const action = params.agent ? 'active' : 'list'
      return callAI(action === 'active' ? 'ai_admin_get' : 'ai_admin_list', {
        params: { resource: 'prompts', action, agent: params.agent },
      })
    }
    return callAI('ai_admin_create', { body: { resource: 'prompts', action: 'save', data: body || {} } })
  }

  if (resource === 'settings') {
    if (method === 'GET') return callAI('ai_admin_list', { params: { resource: 'settings' } })
    if (params.aikey) {
      const apiKey = body?.key || ''
      const data = await callAI('ai_admin_upsert_setting', {
        body: { data: { key: API_KEY_SETTING, value: body?.key || '', isSecret: true, updatedUser: currentUser() } },
      })
      return { ...data, last4: apiKey.slice(-4) }
    }
    return callAI('ai_admin_upsert_setting', {
      body: { data: { key: body?.k, value: body?.v, isSecret: false, updatedUser: currentUser() } },
    })
  }

  if (resource === 'queue' || resource === 'review-queue') {
    if (method === 'GET') return callAI('ai_admin_list', { params: { resource: 'review-queue' } })
    if (params.state) {
      const feedback = String(body?.feedback || body?.reviewFeedback || `Cambio de estado a ${body?.estado || params.state}`).trim()
      return callAI('ai_admin_status', {
        body: {
          resource: 'review-queue',
          action: 'estado',
          id: body?.id,
          data: { estado: body?.estado, feedback },
        },
      })
    }
    return callAI('ai_admin_create', { body: { resource: 'review-queue', action: 'create', data: body || {} } })
  }

  if (resource === 'knowledge') {
    if (method === 'GET') return biaListKnowledge(params)
    if (method === 'POST') return biaCreateKnowledge(body || {})
    if (method === 'PUT') return biaUpdateKnowledge(params.uuid || body?.uuid, body || {})
    if (method === 'DELETE') return biaDeleteKnowledge(params.uuid || body?.uuid)
  }

  throw controlledUnsupported(`Resource no soportado por biaFetch en esta fase: ${resource}`)
}

export async function biaHealth() {
  try {
    const data = await callAI('ai_admin_get', { params: { resource: 'health', action: 'ping' } })
    return { ok: true, db: data?.db || 'up', crypto: data?.crypto || data?.status || 'api', ...data }
  } catch (e) {
    return { ok: false, error: e.message }
  }
}

/* ===== Prompts oficiales (DB) ===== */
export async function biaListPrompts(agent) {
  const data = await callAI('ai_admin_list', { params: { resource: 'prompts', agent } })
  return normalizePromptRows(data)
}

export async function biaGetPrompt(id) {
  if (!id) throw new Error('ID de prompt requerido')
  return callAI('ai_admin_get', { params: { resource: 'prompts', action: 'get', id } })
}

export async function biaGetActivePrompt(agent) {
  const data = await callAI('ai_admin_get', { params: { resource: 'prompts', action: 'active', agent } })
  return normalizePromptContent(data)
}

export async function biaSavePrompt(data) {
  return callAI('ai_admin_create', {
    body: {
      resource: 'prompts',
      action: 'save',
      data: {
        ...data,
        createdUser: data.createdUser || currentUser(),
      },
    },
  })
}

export async function biaUpdatePrompt(id, data) {
  if (!id) throw new Error('ID de prompt requerido')
  return callAI('ai_admin_update', {
    body: {
      resource: 'prompts',
      id,
      data: {
        ...data,
        createdUser: data.createdUser || currentUser(),
        updatedUser: data.updatedUser || currentUser(),
      },
    },
  })
}

export async function biaPublishPrompt(agent, content, comment) {
  return biaSavePrompt({
    agent,
    version: 1,
    content,
    comment,
    isActive: false,
    createdUser: currentUser(),
  })
}

/* El backend de generación es async: devuelve {jobId, status: pending} y el
   resultado queda en ai_jobs. Poleamos action=status hasta done/error. */
const AI_JOB_POLL_INTERVAL_MS = 2500
const AI_JOB_POLL_TIMEOUT_MS = 200000

function aiJobSleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/* Cancelación: el botón "Detener" marca este flag y el polling corta con error
   controlado (el job puede seguir server-side, pero el usuario recupera la UI). */
let aiJobCancelRequested = false

export function biaCancelAIJobWait() {
  aiJobCancelRequested = true
}

async function waitForAIJob(resource, initialData, timeoutMs = AI_JOB_POLL_TIMEOUT_MS) {
  if (!initialData || !initialData.jobId || String(initialData.status || '').toLowerCase() === 'done') {
    return initialData
  }
  aiJobCancelRequested = false
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    await aiJobSleep(AI_JOB_POLL_INTERVAL_MS)
    if (aiJobCancelRequested) {
      aiJobCancelRequested = false
      throw new Error('Proceso detenido por el usuario.')
    }
    const statusData = await callAI('ai_admin_get', {
      params: { resource, action: 'status', id: initialData.jobId },
    })
    const status = String(statusData?.status || '').toLowerCase()
    if (status === 'error') {
      throw new Error(statusData?.error || 'La generación IA falló.')
    }
    if (status === 'done') {
      const result = statusData?.result
      return result && typeof result === 'object' ? result : { ok: true, content: String(result || ''), raw: String(result || '') }
    }
  }
  throw new Error('La generación IA superó el tiempo de espera. Reintentá.')
}

export async function biaGenerateAssistant(payload) {
  const data = await callAI('ai_admin_assistant_generate', {
    body: {
      agent: payload.agent,
      generationMode: payload.generationMode || 'natural',
      promptId: payload.promptId,
      language: payload.language,
      input: payload.input,
      context: payload.context || {},
      createdUser: payload.createdUser || currentUser(),
    },
  })
  const result = await waitForAIJob('assistant', data)
  return result && typeof result === 'object' && result.ok === undefined ? { ok: true, ...result } : result
}

export async function biaGenerateImporterAssistant(payload) {
  const data = await callAI('ai_admin_importer_assistant_generate', {
    body: {
      agent: payload.agent,
      generationMode: payload.generationMode || 'import',
      referenceIr: payload.referenceIr || undefined,
      userPrompt: payload.userPrompt,
      language: payload.language,
      systemPrompt: payload.systemPrompt || '',
      expectJson: payload.expectJson !== false,
      model: payload.model || '',
      maxTokens: payload.maxTokens || 12000,
      temperature: payload.temperature ?? 0,
      useKnowledge: payload.useKnowledge !== false,
      knowledgeLimit: payload.knowledgeLimit || 5,
      knowledgeMaxChars: payload.knowledgeMaxChars || 12000,
      createdUser: payload.createdUser || currentUser(),
    },
  })
  return waitForAIJob('importer-assistant', data)
}

export async function biaValidateCustomScriptPublish(payload) {
  return callAI('ai_admin_custom_scripts_validate_publish', { body: payload })
}

export async function biaSaveDraftCustomScript(payload) {
  return callAI('ai_admin_custom_scripts_save_draft', { body: payload })
}

export async function biaPublishTestCustomScript(payload) {
  return callAI('ai_admin_custom_scripts_publish_test', { body: payload })
}

export async function biaPublishReleaseTestCustomScript(payload) {
  return callAI('ai_admin_custom_scripts_publish_release_test', { body: payload })
}

export async function biaPublishReleaseCustomScript(payload) {
  return callAI('ai_admin_custom_scripts_publish_release', { body: payload })
}

export async function biaSeedPrompts() {
  return {
    ok: false,
    pending: true,
    error: 'Seed de prompts pendiente: no existe endpoint /admin/ai permitido para seed en esta fase',
  }
}

/* ===== Settings ===== */
export async function biaGetSettings() {
  const data = await callAI('ai_admin_list', { params: { resource: 'settings' } })
  return normalizeSettings(data)
}

export async function biaSetApiKey(key) {
  const data = await callAI('ai_admin_upsert_setting', {
    body: {
      data: {
        key: API_KEY_SETTING,
        value: key,
        isSecret: true,
        updatedUser: currentUser(),
      },
    },
  })
  return { ...data, last4: key.slice(-4) }
}

export async function biaSetSetting(k, v) {
  return callAI('ai_admin_upsert_setting', {
    body: {
      data: {
        key: k,
        value: v,
        isSecret: k === API_KEY_SETTING,
        updatedUser: currentUser(),
      },
    },
  })
}

/* ===== Knowledge DB/API ===== */
export async function biaListKnowledge(params = {}) {
  const action = params.active ? 'list-active' : 'list'
  const data = await callAI('ai_admin_list', {
    params: {
      resource: 'knowledge',
      action,
      category: params.category,
      tag: params.tag,
      agent: params.agent,
      limit: params.limit,
    },
  })
  return normalizeKnowledgeRows(data)
}


export async function biaListScriptCosts(filter = {}) {
  const data = await callAI('ai_admin_list', {
    body: { resource: 'costs', action: 'list', data: filter },
  })
  return normalizeCostRows(data)
}

export async function biaScriptCostsSummary(days = 90) {
  const data = await callAI('ai_admin_list', {
    body: { resource: 'costs', action: 'summary', data: { days } },
  })
  return normalizeCostRows(data)
}

export async function biaCreateKnowledge(data) {
  return callAI('ai_admin_create', {
    body: { resource: 'knowledge', action: 'save', data },
  })
}

export async function biaUpdateKnowledge(uuid, data) {
  if (!uuid) throw new Error('UUID de knowledge requerido')
  return callAI('ai_admin_update', {
    body: { resource: 'knowledge', uuid, data },
  })
}

export async function biaDeactivateKnowledge(uuid, updatedUser = currentUser()) {
  if (!uuid) throw new Error('UUID de knowledge requerido')
  return callAI('ai_admin_deactivate', {
    body: { resource: 'knowledge', uuid, updatedUser },
  })
}

export async function biaDeleteKnowledge(uuid) {
  if (!uuid) throw new Error('UUID de knowledge requerido')
  return callAI('ai_admin_delete', {
    body: { resource: 'knowledge', uuid },
  })
}

/* ===== Cola de revision dev (fallback localStorage) ===== */
export async function devQueuePush(item) {
  try {
    const response = await callAI('ai_admin_create', {
      body: { resource: 'review-queue', action: 'create', data: queuePayload(item) },
    })
    const persisted = { ...item, id: response.id || item.id }
    cacheDevQueueItem(persisted, 'db')
    return { ok: true, id: persisted.id, via: 'db' }
  } catch (e) {
    cacheDevQueueItem(item, 'local')
    return { ok: true, id: item.id, via: 'localStorage', warn: e.message }
  }
}

export async function devQueueSaveFiles(item) {
  const payload = queuePayload(item)
  if (!payload.id) throw new Error('id de revision requerido')
  return callAI('ai_admin_update', {
    body: {
      resource: 'review-queue',
      action: 'update',
      id: payload.id,
      data: payload,
    },
  })
}

export async function devQueueList() {
  try {
    const data = await callAI('ai_admin_list', { params: { resource: 'review-queue' } })
    const rows = Array.isArray(data) ? data : data.items || []
    let localItems = []
    try { localItems = JSON.parse(localStorage.getItem(LS_KEY) || '[]') } catch (_) {}
    return { items: mergeDevQueueItems(rows.map(normalizeQueueItem), localItems), via: 'db' }
  } catch (e) {
    try {
      return { items: JSON.parse(localStorage.getItem(LS_KEY) || '[]'), via: 'localStorage', warn: e.message }
    } catch (_) {
      return { items: [], via: 'localStorage', warn: e.message }
    }
  }
}

export async function devQueuePendingSummary(options = {}) {
  const minIntervalMs = Math.max(90000, Number(options.minIntervalMs || 90000))
  const now = Date.now()
  if (!options.force && now - lastDevQueueSummaryAt < minIntervalMs) return cachedDevQueueSummary
  if (pendingDevQueueSummary) return pendingDevQueueSummary

  const isPending = (item) => String(item?.estado || 'en_revision').trim().toLowerCase() === 'en_revision'
  const summaryFromItems = (items, via, warn = '') => {
    const pending = (items || []).filter(isPending)
    const ids = pending.map((item) => String(item.id || '')).filter(Boolean)
    const latestTs = pending.reduce((latest, item) => {
      const value = item.ts || item.createdDate || item.created_date || ''
      return value && String(value) > String(latest || '') ? value : latest
    }, '')
    return { count: pending.length, ids, latestTs, via, warn }
  }

  pendingDevQueueSummary = callAI('ai_admin_list', { params: { resource: 'review-queue', estado: 'en_revision' } })
    .then((data) => {
      const rows = Array.isArray(data) ? data : data.items || []
      cachedDevQueueSummary = summaryFromItems(rows.map(normalizeQueueItem), 'db')
      lastDevQueueSummaryAt = Date.now()
      return cachedDevQueueSummary
    })
    .catch((e) => {
      try {
        const items = JSON.parse(localStorage.getItem(LS_KEY) || '[]')
        cachedDevQueueSummary = summaryFromItems(items, 'localStorage', e.message)
      } catch (_) {
        cachedDevQueueSummary = { count: 0, ids: [], latestTs: '', via: 'localStorage', warn: e.message }
      }
      lastDevQueueSummaryAt = Date.now()
      return cachedDevQueueSummary
    })
    .finally(() => {
      pendingDevQueueSummary = null
    })

  return pendingDevQueueSummary
}

export async function devQueueDelete(id) {
  if (!id) throw new Error('ID de revisión requerido')
  return callAI('ai_admin_update', { body: { resource: 'review-queue', action: 'delete', id } })
}

export async function devQueueSetState(id, estado, feedback) {
  try {
    const cleanFeedback = String(feedback || `Cambio de estado a ${estado}`).trim()
    await callAI('ai_admin_status', {
      body: {
        resource: 'review-queue',
        action: 'estado',
        id,
        data: { estado, feedback: cleanFeedback },
      },
    })
    return { ok: true, via: 'db' }
  } catch (e) {
    return { ok: false, via: 'localStorage', warn: e.message }
  }
}

export async function devQueueSendMessage(id, message) {
  if (!id) throw new Error('ID de revisión requerido')
  const cleanMessage = String(message || '').trim()
  if (!cleanMessage) throw new Error('Mensaje requerido')
  return callAI('ai_admin_create', {
    body: {
      resource: 'review-queue',
      action: 'message',
      id,
      data: {
        reviewId: id,
        message: cleanMessage,
        senderType: 'dev',
        senderUsername: 'dev_review',
      },
    },
  })
}

export async function devQueueMarkMessagesRead(id) {
  if (!id) throw new Error('ID de revisión requerido')
  return callAI('ai_admin_status', {
    body: {
      resource: 'review-queue',
      action: 'messages-read',
      id,
      data: {
        reviewId: id,
        readerType: 'dev',
      },
    },
  })
}
